import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild,
  ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2,
  ViewEncapsulation
} from '@angular/core';
import { Subject } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { takeUntil } from 'rxjs/operators';

import { AnimationCurves, AnimationDurations } from '../../../core/animation/animation';
import { HeaderComponent } from '../header/header.component';

@Component({
  selector: 'mcrai-panel',
  template: `<ng-content select="mcrai-panel-header"></ng-content>

<div class="mcrai-panel__overflow" [@slide]="!isCollapsed()">
  <ng-content select="mcrai-panel-actions[top]"></ng-content>
  <ng-content select="mcrai-panel-content"></ng-content>
  <ng-content select="mcrai-panel-footer"></ng-content>
  <ng-content select="mcrai-panel-actions"></ng-content>
</div>

`,
  styles: [`.mcrai-panel{display:flex;flex-direction:column;align-items:stretch;height:100%}.mcrai-panel[collapsable]{height:auto}.mcrai-panel__overflow{overflow:hidden;display:flex;flex-direction:column;flex:1 1 auto}`],
  preserveWhitespaces: false,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slide', [
      state('true',  style({ height: '*' })),
      state('false', style({ height: 0 })),
      transition('0 => 1',
        animate(`${AnimationDurations.ENTERING} ${AnimationCurves.STANDARD_CURVE}`)),
      transition('1 => 0',
        animate(`${AnimationDurations.ENTERING} ${AnimationCurves.STANDARD_CURVE}`)),
    ])
  ]
})
export class PanelComponent implements OnInit, OnDestroy, AfterViewInit, AfterContentInit {
  readonly PANEL_CLASS = 'mcrai-panel';

  private _collapsable = false;
  private _collapsed   = false;
  private _destroy     = new Subject();

  @Input() set color(value: string) {
    this._renderer
      .addClass(this._elRef.nativeElement, `${this.PANEL_CLASS}--${value}`);
  }

  @Input() set collapsable(value: any) {
    if (value !== 'undefined' && value !== false) {
      this._collapsable = true;
    }
  }

  @Input() set collapsed(value: boolean) {
    this._collapsed = value;
  }

  @Output() collapsedEmitter: EventEmitter<Boolean> = new EventEmitter();

  @ContentChild(HeaderComponent) header: HeaderComponent;

  constructor(
    private _renderer: Renderer2,
    private _elRef: ElementRef,
    private _changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this._renderer
      .addClass(this._elRef.nativeElement, this.PANEL_CLASS);
  }

  ngAfterContentInit() {
    if (this._collapsable) {
      this.header.setCollapsable();
    }

    if (this._collapsed) {
      this.header.setCollapsed(this._collapsed);
    }
  }

  ngAfterViewInit() {
    if (this.header) {
      this.header
        .clickEvent
        .pipe(
          takeUntil(this._destroy)
        )
        .subscribe(() => this.toggleCollapsed());
    }
  }

  ngOnDestroy() {
    this._destroy.next();
  }

  public isCollapsed(): boolean {
    return this._collapsable && this._collapsed;
  }

  public toggleCollapsed(): void {
    if (this._collapsable) {
      this._collapsed = !this._collapsed;
      this.header.setCollapsed(this._collapsed);

      if (!this._changeDetectorRef['destroyed']) {
        this._changeDetectorRef.detectChanges();
      }
    }

    this.collapsedEmitter.emit(this._collapsed);
  }

  public open(): void {
    if (this._collapsable) {
      this._collapsed = false;
      this.header.setCollapsed(false);

      if (!this._changeDetectorRef['destroyed']) {
        this._changeDetectorRef.detectChanges();
      }
    }
  }

  public close(): void {
    if (this._collapsable) {
      this._collapsed = true;
      this.header.setCollapsed(true);

      if (!this._changeDetectorRef['destroyed']) {
        this._changeDetectorRef.detectChanges();
      }
    }
  }
}
